import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Tabs,
  Tab,
  CircularProgress,
  Alert
} from '@mui/material';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import EmailTemplateEditor from '../templates/EmailTemplateEditor';
import EmailTemplateService from '../../../services/EmailTemplateService';
import EmailListService from '../../../services/EmailListService';
import EmailMarketingService from '../../../services/EmailMarketingService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import dayjs from 'dayjs';

const SendTab = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [lists, setLists] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadingLists, setLoadingLists] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    template: '',
    list: '',
    subject: '',
    htmlContent: '',
    scheduled: false,
    scheduledDate: null
  });

  // Cargar plantillas del backend
  const loadTemplates = useCallback(async () => {
    try {
      setLoadingTemplates(true);
      setError(null);
      const response = await EmailTemplateService.getTemplates(1, 100, '');
      
      // El backend puede devolver {data: [...], total: number} o directamente un array
      const templatesData = response.data || response;
      const templatesArray = Array.isArray(templatesData) ? templatesData : [];
      
      setTemplates(templatesArray);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError(t('error_loading_templates') || 'Error al cargar las plantillas');
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, [t]);

  // Cargar listas de correos del backend
  const loadLists = useCallback(async () => {
    try {
      setLoadingLists(true);
      setError(null);
      const response = await EmailListService.getLists(1, 100, '');
      
      // El servicio devuelve { data: [...], total, page, pageSize }
      const listsArray = response.data || [];
      
      setLists(listsArray);
    } catch (err) {
      console.error('Error loading email lists:', err);
      setError(t('error_loading_email_lists') || 'Error al cargar las listas de correos');
      setLists([]);
    } finally {
      setLoadingLists(false);
    }
  }, [t]);

  // Cargar datos al montar el componente
  useEffect(() => {
    loadTemplates();
    loadLists();
  }, [loadTemplates, loadLists]);

  // Cuando se selecciona una plantilla, cargar su contenido
  useEffect(() => {
    if (formData.template) {
      const selectedTemplate = templates.find(t => t.id === parseInt(formData.template));
      if (selectedTemplate) {
        setFormData(prev => ({
          ...prev,
          subject: selectedTemplate.subject || prev.subject,
          htmlContent: selectedTemplate.htmlContent || prev.htmlContent
        }));
      }
    }
  }, [formData.template, templates]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validaciones
    if (!formData.template) {
      setError(t('template_required') || 'Debes seleccionar una plantilla');
      return;
    }

    if (!formData.list) {
      setError(t('list_required') || 'Debes seleccionar una lista de correos');
      return;
    }

    if (!formData.subject || !formData.subject.trim()) {
      setError(t('subject_required') || 'Debes ingresar un asunto');
      return;
    }

    if (!formData.htmlContent || !formData.htmlContent.trim()) {
      setError(t('content_required') || 'El contenido del correo no puede estar vacío');
      return;
    }

    if (formData.scheduled && !formData.scheduledDate) {
      setError(t('scheduled_date_required') || 'Debes seleccionar una fecha y hora para programar el envío');
      return;
    }

    try {
      setSending(true);
      setError(null);

      const emailData = {
        templateId: parseInt(formData.template),
        listId: parseInt(formData.list),
        subject: formData.subject.trim(),
        htmlContent: formData.htmlContent.trim(),
        scheduled: formData.scheduled,
        scheduledDate: formData.scheduled ? formData.scheduledDate : null
      };

      await EmailMarketingService.sendEmail(emailData);

      SnackbarUtils.success(
        formData.scheduled 
          ? (t('email_scheduled_successfully') || 'Correo programado exitosamente')
          : (t('email_sent_successfully') || 'Correo enviado exitosamente')
      );

      // Limpiar formulario después del envío exitoso
      setFormData({
        template: '',
        list: '',
        subject: '',
        htmlContent: '',
        scheduled: false,
        scheduledDate: null
      });
    } catch (err) {
      console.error('Error sending email:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      setError(errorMessage || (t('error_sending_email') || 'Error al enviar el correo'));
      SnackbarUtils.error(errorMessage || (t('error_sending_email') || 'Error al enviar el correo'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Paper sx={{ padding: 3, mt: 2 }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            {t('email_configuration')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid container spacing={3} item xs={12}>
          <Grid item xs={6}>
            <FormControlLabel style={{ display: 'flex', alignItems: 'center', height: '56px' }}
              control={
                <Switch
                  checked={formData.scheduled}
                  onChange={(e) => handleChange('scheduled', e.target.checked)}
                />
              }
              label={t('schedule_email')}
            />
          </Grid>
          {formData.scheduled && (
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label={t('scheduled_date_time')}
                  value={formData.scheduledDate ? dayjs(formData.scheduledDate) : null}
                  onChange={(date) => handleChange('scheduledDate', date ? date.toISOString() : null)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  minDateTime={dayjs()}
                />
              </LocalizationProvider>
            </Grid>
          )}
        </Grid>
        
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>{t('template')}</InputLabel>
            <Select
              value={formData.template}
              label={t('template')}
              onChange={(e) => handleChange('template', e.target.value)}
              disabled={loadingTemplates}
            >
              {loadingTemplates ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {t('loading') || 'Cargando...'}
                </MenuItem>
              ) : (
                templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>{t('email_list')}</InputLabel>
            <Select
              value={formData.list}
              label={t('email_list')}
              onChange={(e) => handleChange('list', e.target.value)}
              disabled={loadingLists}
            >
              {loadingLists ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {t('loading') || 'Cargando...'}
                </MenuItem>
              ) : (
                lists.map((list) => (
                  <MenuItem key={list.id} value={list.id}>
                    {list.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('subject')}
            value={formData.subject}
            onChange={(e) => handleChange('subject', e.target.value)}
            placeholder={t('email_subject_placeholder')}
          />
        </Grid>

        {formData.template && (
          <Grid item xs={12}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                <Tab label={t('edit_content')} />
                <Tab label={t('preview')} />
              </Tabs>
            </Box>

            {activeTab === 0 && (
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={16}
                  label={t('email_content')}
                  value={formData.htmlContent}
                  onChange={(e) => handleChange('htmlContent', e.target.value)}
                  placeholder={t('html_content_placeholder')}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: '0.875rem'
                    }
                  }}
                />
              </Box>
            )}

            {activeTab === 1 && (
              <Paper sx={{ p: 2, minHeight: 300, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom>
                  {formData.subject || t('email_preview')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box
                  dangerouslySetInnerHTML={{ __html: formData.htmlContent || t('no_content') }}
                  sx={{
                    '& img': { maxWidth: '100%' },
                    '& a': { color: 'primary.main' }
                  }}
                />
              </Paper>
            )}
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button variant="outlined" onClick={() => setFormData({
              template: '',
              list: '',
              subject: '',
              scheduled: false,
              scheduledDate: null
            })}>
              {t('cancel')}
            </Button>
            <Button
              variant="contained"
              startIcon={getIcon('mail')}
              onClick={handleSubmit}
              disabled={sending || !formData.template || !formData.list || !formData.subject || !formData.htmlContent}
            >
              {sending 
                ? (t('sending') || 'Enviando...') 
                : (formData.scheduled ? t('schedule') : t('send_now'))
              }
            </Button>
          </Box>
        </Grid>
      </Grid>

      <EmailTemplateEditor
        open={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        template={{
          name: templates.find(t => t.id === parseInt(formData.template))?.name || '',
          subject: formData.subject,
          htmlContent: formData.htmlContent
        }}
        onSave={(templateData) => {
          handleChange('subject', templateData.subject);
          handleChange('htmlContent', templateData.htmlContent);
          setIsEditorOpen(false);
        }}
        mode="edit"
      />
    </Paper>
  );
};

export default SendTab;

